import api from '../api/apiClient';
import { MessageAttachment } from './chatService';

const MAX_VIDEO_BYTES = 50 * 1024 * 1024;
const MAX_VIDEO_DURATION_SEC = 60;
// Cloudinary Free plan limit per image (backend enforces the same).
const MAX_IMAGE_BYTES = 10 * 1024 * 1024;

const getVideoDuration = (file: File): Promise<number> =>
  new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(video.src);
      resolve(video.duration);
    };
    video.onerror = () => {
      URL.revokeObjectURL(video.src);
      reject(new Error('Could not read video metadata.'));
    };
    video.src = URL.createObjectURL(file);
  });

const getImageDimensions = (file: File): Promise<{ width: number; height: number }> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(img.src);
      resolve({ width: img.width, height: img.height });
    };
    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      reject(new Error('Could not read image dimensions.'));
    };
    img.src = URL.createObjectURL(file);
  });

export const validateChatMediaFile = async (file: File): Promise<void> => {
  const isVideo = file.type.startsWith('video/');
  const isImage = file.type.startsWith('image/');

  if (!isVideo && !isImage) {
    throw new Error('Only image and video files are supported.');
  }

  if (isImage && file.size > MAX_IMAGE_BYTES) {
    throw new Error('Images must be 10MB or smaller.');
  }

  if (isVideo) {
    if (file.size > MAX_VIDEO_BYTES) {
      throw new Error('Videos must be 50MB or smaller.');
    }
    const duration = await getVideoDuration(file);
    if (duration > MAX_VIDEO_DURATION_SEC) {
      throw new Error('Videos must be 60 seconds or shorter.');
    }
  }
};

export const uploadChatMediaFile = async (
  file: File,
  onProgress?: (progress: number) => void
): Promise<MessageAttachment> => {
  await validateChatMediaFile(file);

  const isVideo = file.type.startsWith('video/');
  const formData = new FormData();
  const fieldName = isVideo ? 'video' : 'image';

  formData.append(fieldName, file);

  if (!isVideo) {
    const { width, height } = await getImageDimensions(file);
    formData.append('width', String(width));
    formData.append('height', String(height));
  } else {
    const duration = await getVideoDuration(file);
    formData.append('duration', String(duration));
  }

  onProgress?.(0.1);

  const response = isVideo
    ? await api.chat.uploadVideo(formData)
    : await api.chat.uploadImage(formData);

  onProgress?.(1);

  return response.data.data as MessageAttachment;
};
